import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { axiosInstance } from "../App";
import { toast } from "sonner";
import { History, Eye, Trash2, Banknote, TrendingDown, Search, Filter, Edit } from "lucide-react";
import { CurrencyDisplay } from "./CurrencySettings";
import { formatDateDDMonYYYY } from "../utils/dateFormat";
import DateFilter from "./DateFilter";

const CashHandoverHistory = ({ user }) => {
  const [handovers, setHandovers] = useState([]);
  const [filteredHandovers, setFilteredHandovers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [viewDialog, setViewDialog] = useState({ isOpen: false, handover: null });
  const [editDialog, setEditDialog] = useState({ isOpen: false, handover: null });
  const [deleteDialog, setDeleteDialog] = useState({ isOpen: false, handoverId: null });
  const [editFormData, setEditFormData] = useState({
    handover_date: "",
    handed_over_to: "",
    remarks: "",
    cash_handed_over: 0
  });

  // Check if user can edit (admin/superuser only)
  const canEdit = user?.role === 'admin' || user?.role === 'superuser';

  useEffect(() => {
    fetchHandovers();
  }, [startDate, endDate]);

  useEffect(() => {
    filterHandovers();
  }, [handovers, searchTerm]);

  const fetchHandovers = async () => {
    setLoading(true);
    try {
      let url = "/cash-handover";
      const params = new URLSearchParams();
      if (startDate) params.append("start_date", startDate);
      if (endDate) params.append("end_date", endDate);
      if (params.toString()) url += `?${params.toString()}`;
      
      const response = await axiosInstance.get(url);
      setHandovers(response.data);
    } catch (error) {
      toast.error("Failed to fetch handover history");
    } finally {
      setLoading(false);
    }
  };

  const filterHandovers = () => {
    let filtered = [...handovers];
    
    if (searchTerm) {
      filtered = filtered.filter(h =>
        h.handed_over_by_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        h.handed_over_to?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        h.remarks?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        h.handover_date?.includes(searchTerm)
      );
    }
    
    setFilteredHandovers(filtered);
  };

  const handleEdit = (handover) => {
    setEditFormData({
      handover_date: handover.handover_date,
      handed_over_to: handover.handed_over_to || "",
      remarks: handover.remarks || "",
      cash_handed_over: handover.cash_handed_over
    });
    setEditDialog({ isOpen: true, handover });
  };

  const handleEditSubmit = async () => {
    try {
      await axiosInstance.put(`/cash-handover/${editDialog.handover.id}`, editFormData);
      toast.success("Handover details updated successfully");
      setEditDialog({ isOpen: false, handover: null });
      fetchHandovers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update handover");
    }
  };

  const handleDelete = async () => {
    try {
      await axiosInstance.delete(`/cash-handover/${deleteDialog.handoverId}`);
      toast.success("Handover deleted successfully");
      setDeleteDialog({ isOpen: false, handoverId: null });
      fetchHandovers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete handover");
    }
  };

  const totalHandedOver = filteredHandovers.reduce((sum, h) => sum + h.cash_handed_over, 0);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">Cash Handover History</h2>
          <p className="text-muted-foreground">View all cash handover transactions</p>
        </div>
      </div>

      {/* Summary Card */}
      <Card className="shadow-lg border-l-4 border-l-purple-500 bg-gradient-to-r from-purple-50 to-pink-50">
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
                <TrendingDown className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Handed Over</p>
                <p className="text-2xl font-bold text-purple-600">
                  <CurrencyDisplay amount={totalHandedOver} />
                </p>
              </div>
            </div>
            <Badge variant="secondary" className="text-lg px-4 py-2">
              {filteredHandovers.length} entries
            </Badge>
          </div>
        </CardContent>
      </Card>

      {/* Filters */}
      <Card className="shadow-lg">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Filter className="w-5 h-5" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                placeholder="Search by name, remarks..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <DateFilter
              label="Date Range"
              onFilterChange={(filterData) => {
                if (filterData.filterType === 'exact') {
                  setStartDate(filterData.exactDate);
                  setEndDate(filterData.exactDate);
                } else {
                  setStartDate(filterData.startDate || "");
                  setEndDate(filterData.endDate || "");
                }
              }}
            />
          </div>
        </CardContent>
      </Card>

      {/* History Table */}
      <Card className="shadow-xl">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <span className="text-gray-500">Loading...</span>
            </div>
          ) : filteredHandovers.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12">
              <History className="w-16 h-16 text-gray-300 mb-4" />
              <p className="text-gray-500">No handover records found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead>Date</TableHead>
                    <TableHead>Handed Over By</TableHead>
                    <TableHead>Handed To</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead className="text-right">Balance Before</TableHead>
                    <TableHead className="text-right">Balance After</TableHead>
                    <TableHead className="text-center">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredHandovers.map((handover, index) => (
                    <TableRow key={handover.id} className={`${index % 2 === 0 ? "bg-white" : "bg-gray-50"} cursor-pointer hover:bg-blue-50/50 transition-colors`} onClick={() => setViewDialog({ isOpen: true, handover })}>
                      <TableCell className="font-medium">
                        {formatDateDDMonYYYY(handover.handover_date)}
                      </TableCell>
                      <TableCell>{handover.handed_over_by_name}</TableCell>
                      <TableCell>{handover.handed_over_to}</TableCell>
                      <TableCell className="text-right font-bold text-purple-600">
                        <CurrencyDisplay amount={handover.cash_handed_over} />
                        {handover.restaurant_amounts?.length > 0 && (
                          <span className="block text-xs font-normal text-gray-400">{handover.restaurant_amounts.length} restaurant{handover.restaurant_amounts.length > 1 ? 's' : ''}</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right text-gray-600">
                        <CurrencyDisplay amount={handover.balance_before || 0} />
                      </TableCell>
                      <TableCell className="text-right text-green-600">
                        <CurrencyDisplay amount={handover.balance_after || 0} />
                      </TableCell>
                      <TableCell className="text-center" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-center gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setViewDialog({ isOpen: true, handover })}
                            data-testid={`view-handover-${handover.id}`}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          {canEdit && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                              onClick={() => handleEdit(handover)}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                          )}
                          {canEdit && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              onClick={() => setDeleteDialog({ isOpen: true, handoverId: handover.id })}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* View Dialog */}
      <Dialog open={viewDialog.isOpen} onOpenChange={(open) => !open && setViewDialog({ isOpen: false, handover: null })}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Banknote className="w-5 h-5" />
              Handover Details
            </DialogTitle>
          </DialogHeader>
          {viewDialog.handover && (
            <div className="space-y-5">
              {/* Total Amount */}
              <div className="p-4 rounded-lg bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-100 text-center">
                <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Total Amount</p>
                <p className="text-3xl font-bold text-purple-600" data-testid="handover-detail-total">
                  <CurrencyDisplay amount={viewDialog.handover.cash_handed_over} />
                </p>
              </div>

              {/* Restaurant Breakdown */}
              {viewDialog.handover.restaurant_amounts?.length > 0 && (
                <div>
                  <p className="text-sm font-semibold text-gray-700 mb-2">Restaurant Breakdown</p>
                  <div className="space-y-2">
                    {viewDialog.handover.restaurant_amounts.map((ra, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2.5 rounded-lg border bg-gray-50/80" data-testid={`handover-detail-restaurant-${idx}`}>
                        <span className="text-sm font-medium text-gray-700">{ra.restaurant_name || 'Unknown'}</span>
                        <span className="text-sm font-bold text-purple-600"><CurrencyDisplay amount={ra.amount} /></span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Info Grid */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-gray-400 uppercase">Date</Label>
                  <p className="font-medium text-sm">{formatDateDDMonYYYY(viewDialog.handover.handover_date)}</p>
                </div>
                <div>
                  <Label className="text-xs text-gray-400 uppercase">Handed To</Label>
                  <p className="font-medium text-sm">{viewDialog.handover.handed_over_to}</p>
                </div>
                <div>
                  <Label className="text-xs text-gray-400 uppercase">Handed By</Label>
                  <p className="font-medium text-sm">{viewDialog.handover.handed_over_by_name}</p>
                </div>
                <div>
                  <Label className="text-xs text-gray-400 uppercase">Balance Before</Label>
                  <p className="font-medium text-sm"><CurrencyDisplay amount={viewDialog.handover.balance_before || 0} /></p>
                </div>
              </div>
              {viewDialog.handover.remarks && (
                <div>
                  <Label className="text-xs text-gray-400 uppercase">Remarks</Label>
                  <p className="text-sm font-medium mt-1">{viewDialog.handover.remarks}</p>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setViewDialog({ isOpen: false, handover: null })}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialog.isOpen} onOpenChange={(open) => !open && setDeleteDialog({ isOpen: false, handoverId: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Handover Record</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this handover record? This will affect the cash ledger balance calculations.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Edit Dialog - Only for Admin */}
      <Dialog open={editDialog.isOpen} onOpenChange={(open) => !open && setEditDialog({ isOpen: false, handover: null })}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5 text-blue-600" />
              Edit Handover Details
            </DialogTitle>
            <DialogDescription>
              Update handover details. Note: Amount cannot be modified.
            </DialogDescription>
          </DialogHeader>
          {editDialog.handover && (
            <div className="space-y-4">
              {/* Amount - Editable by Admin */}
              <div className="space-y-2">
                <Label>Amount</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={editFormData.cash_handed_over}
                  onChange={(e) => setEditFormData({ ...editFormData, cash_handed_over: parseFloat(e.target.value) || 0 })}
                />
              </div>
              
              {/* Editable Fields */}
              <div className="space-y-2">
                <Label>Handover Date</Label>
                <Input
                  type="date"
                  value={editFormData.handover_date}
                  onChange={(e) => setEditFormData({ ...editFormData, handover_date: e.target.value })}
                />
              </div>
              
              <div className="space-y-2">
                <Label>Handed Over To</Label>
                <Input
                  placeholder="Enter recipient name"
                  value={editFormData.handed_over_to}
                  onChange={(e) => setEditFormData({ ...editFormData, handed_over_to: e.target.value })}
                />
              </div>
              
              <div className="space-y-2">
                <Label>Remarks</Label>
                <Input
                  placeholder="Enter remarks"
                  value={editFormData.remarks}
                  onChange={(e) => setEditFormData({ ...editFormData, remarks: e.target.value })}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialog({ isOpen: false, handover: null })}>
              Cancel
            </Button>
            <Button onClick={handleEditSubmit} className="bg-blue-600 hover:bg-blue-700">
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CashHandoverHistory;
